import React from 'react';
import { ArcSocketContext } from '@src/core/socket';
import { useLocation, useSearchParams } from 'react-router-dom';
import { routesConfig } from '@src/core/routes';
import { PointEntity } from '@src/features/order/entities';
import { toast } from 'react-toastify';
import { useGoHereClearance } from '@src/features/drone/hooks/useGoHereClearance';
import {
  registerGoHereReset,
  unregisterGoHereReset,
} from '@src/features/drone/utils/go-here-registry';

const useGoTo = () => {
  const [searchParams] = useSearchParams();
  const { pathname } = useLocation();
  const onDronesPage = pathname.startsWith(routesConfig.drones);

  const { arcSocket } = React.useContext(ArcSocketContext);
  
  const [pointsByDrone, setPointsByDrone] = React.useState<Record<string, PointEntity[]>>({});
  const [positionByDrone, setPositionByDrone] = React.useState<Record<string, google.maps.LatLngLiteral | null>>({});

  const droneSerial = searchParams.get('droneSerial');

  // Get current points and position for the selected drone
  const points = React.useMemo(() => {
    return droneSerial ? (pointsByDrone[droneSerial] ?? []) : [];
  }, [droneSerial, pointsByDrone]);

  const position = React.useMemo(() => {
    return droneSerial ? (positionByDrone[droneSerial] ?? null) : null;
  }, [droneSerial, positionByDrone]);

  const removePosition = React.useCallback(() => {
    if (!droneSerial) return;
    setPositionByDrone(prev => ({
      ...prev,
      [droneSerial]: null,
    }));
  }, [droneSerial]);

  const addPosition = React.useCallback(async (e: google.maps.LatLngLiteral) => {
    if (!droneSerial) return;
    setPositionByDrone(prev => ({
      ...prev,
      [droneSerial]: e,
    }));
  }, [droneSerial]);

  const resetGoHere = React.useCallback(() => {
    if (!droneSerial) return;
    setPointsByDrone(prev => {
      const updated = { ...prev };
      delete updated[droneSerial];
      return updated;
    });
    setPositionByDrone(prev => ({
      ...prev,
      [droneSerial]: null,
    }));
  }, [droneSerial]);

  useGoHereClearance({ resetGoHere, points, serialNumber: droneSerial });

  React.useEffect(() => {
    if (!droneSerial) return;
    registerGoHereReset(droneSerial, resetGoHere);
    return () => unregisterGoHereReset(droneSerial);
  }, [droneSerial, resetGoHere]);

  const goTo = React.useCallback((e: google.maps.LatLngLiteral) => {
    const socket = arcSocket();
    const serialNumber = searchParams.get('droneSerial');

    if (!onDronesPage || !serialNumber) {
      toast.error('Select a drone before using Go Here.');
      return;
    }

    if (!socket || socket.readyState !== WebSocket.OPEN) {
      toast.error('Connection unavailable. Try again in a moment.');
      return;
    }

    const point: PointEntity = {
      id: Date.now(),
      position: {
        lat: e.lat,
        lng: e.lng,
      },
    };

    // Replace any previous Go Here target for this drone (only one active marker).
    setPointsByDrone(prev => ({
      ...prev,
      [serialNumber]: [point],
    }));
    
    socket.send(
      JSON.stringify({
        serialNumber,
        type: "COMMAND",
        command: "GOTO",
        latitude_deg: e.lat,
        longitude_deg: e.lng,
      })
    );
  }, [arcSocket, onDronesPage, searchParams]);
 
  return {
    goTo,
    points, 
    position,
    resetGoHere,
    addPosition,
    removePosition,
  };
};

export default useGoTo;